import { Text } from '@/components/commons/typography/Text'
import { Title } from '@/components/commons/typography/Title'
import {
  EditIconPen,
  ShareArrow,
  TrashCanIcon,
} from '@/public/assets/v2/images/icons'
import { cssClass } from '@/shared/utils'
import { highlightHeaderText } from 'containers/highlights/styles/highlight.css'
import { Highlight } from 'containers/highlights/types/highlights'
import { getDateFormat } from 'utilities/dates'
import { HighlightThumbnail } from './HighlightThumbnail'
import { HighlightCardContent } from './HighlighCardContent'
import { motion } from 'framer-motion'
import {
  useIsOwnProfile,
  useProfile,
} from 'containers/profile/hooks/useProfile'
import { forwardRef, useState } from 'react'
import { HighlightShareModal } from './HighlightShareModal'
import { HighlightDeleteModal } from './HighlightDeleteModal'
import { useDeleteHighlight } from 'containers/highlights/hooks/useHighlights'
import { User } from 'containers/user/types/user'
import Link from 'next/link'
import { joinWithSlash } from 'utilities/links'
import { useViewPHighlightRequest } from 'containers/highlights/hooks/useViewHighlight'
import { Likes } from 'containers/likes'
import { SurfaceType } from 'containers/likes/types/likes'

interface HighlightCardProps {
  highlight: Highlight
  defaultOpen?: boolean
  isEditingList?: boolean
  useRemoveFromProject?: boolean
  overrideProfile?: User
  withBorder?: boolean
  onOpen?: () => void
  onEdit?: () => void
  onRemove?: () => void
}

export const HighlightCard = forwardRef(
  ({
    highlight,
    defaultOpen,
    useRemoveFromProject,
    onOpen,
    onEdit,
    isEditingList,
    withBorder,
    onRemove,
    overrideProfile,
  }: HighlightCardProps) => {
    const { profile } = useProfile()
    const isOwnProfile = useIsOwnProfile()
    const [shareModalOpen, setShareModalOpen] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const { mutateAsync, isLoading } = useDeleteHighlight()

    useViewPHighlightRequest({
      ownerProfile: overrideProfile || profile,
      highlight,
    })

    const deleteHighlight = async () => {
      // If the highlight is in project, we don't want to delete it,
      // we want to remove it from the project
      if (useRemoveFromProject && onRemove) {
        onRemove()
        return
      }

      await mutateAsync(highlight.id)
      setShowDeleteModal(false)
    }

    return (
      <>
        <HighlightDeleteModal
          onConfirm={deleteHighlight}
          close={() => setShowDeleteModal(false)}
          opened={showDeleteModal}
          isLoading={isLoading}
          useRemoveOnProject={useRemoveFromProject}
        />
        <motion.div
          layoutId={highlight?.id}
          className={cssClass([
            'full-w bg-white-primary rounded-12',
            withBorder && 'border border-solid border-secondary',
          ])}
        >
          <div
            className="flex items-start justify-space-between p-12"
            data-highlight-id={highlight?.id}
          >
            <div
              className={cssClass([
                'flex flex-column gap-4',
                highlightHeaderText,
              ])}
            >
              <Title as="h2" variation="small">
                {overrideProfile ? (
                  <Link
                    href={joinWithSlash(
                      overrideProfile?.username?.toLocaleLowerCase(),
                      'highlights',
                      highlight?.id
                    )}
                    className="font-bold"
                  >
                    {highlight.title}
                  </Link>
                ) : (
                  highlight.title
                )}
              </Title>
              <Text
                sizeClass="text-12"
                colorClass="text-tertirary"
                className="break-text"
              >
                Posted{' '}
                {getDateFormat({ date: highlight.createdOn, short: true })}
              </Text>
            </div>
            <div className="flex gap-8">
              {!isEditingList && (
                <ShareArrow
                  className="cursor-pointer text-22"
                  onClick={() => setShareModalOpen(true)}
                />
              )}
              {isOwnProfile && isEditingList && (
                <>
                  <EditIconPen
                    className="cursor-pointer text-22"
                    onClick={onEdit}
                  />

                  <TrashCanIcon
                    className="cursor-pointer text-22 text-danger"
                    onClick={() => setShowDeleteModal(true)}
                  />
                </>
              )}
            </div>
          </div>
          <HighlightThumbnail highlight={highlight} onClick={onOpen} />
          <Likes
            surfaceType={SurfaceType.HIGHLIGHT}
            referencedId={highlight.id}
          />
          <HighlightCardContent
            overrideProfile={overrideProfile}
            highlight={highlight}
            defaultOpen={defaultOpen}
          />
        </motion.div>
        <HighlightShareModal
          isOpen={shareModalOpen}
          overrideProfile={overrideProfile}
          closeModal={() => setShareModalOpen(false)}
          highlight={highlight}
        />
      </>
    )
  }
)
